import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MerchantDetail } from '@components/templates/marketplace';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetStoreDetail } from '@services/useGetStoreDetail';
import { useSEOMetatags } from '@hooks/useSEOMetatags';
import { useParams } from 'react-router';

export function MerchantDetailPage() {
    const { t, i18n } = useTranslation();
    const { allSettings, storeDetail, isLoadingStoreDetail } = useData();
    const { request: requestStore } = useGetStoreDetail();
    const { uuid } = useParams();

    const translations = {
        title: t('merchant-detail-component.metatags.title', {
            name: storeDetail?.data?.commercial_name,
            marketplace: allSettings?.data?.marketplaceName,
        }),
        description: t('merchant-detail-component.metatags.description', {
            name: storeDetail?.data?.commercial_name,
            marketplace: allSettings?.data?.marketplaceName,
        }),
        servicesTitle: t('merchant-detail-component.metatags.title-services', {
            name: storeDetail?.data?.commercial_name,
            location: storeDetail?.data?.address?.city,
            marketplace: allSettings?.data?.marketplaceName,
        }),
        servicesDescription: t('merchant-detail-component.metatags.description-services', {
            name: storeDetail?.data?.commercial_name,
            marketplace: allSettings?.data?.marketplaceName,
        }),
    };

    // Fetch store data based on UUID
    useEffect(() => {
        if (uuid) {
            requestStore(uuid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid, i18n.language]);

    const merchantDetail = {
        uuid: storeDetail?.data?.uuid ?? '',
        name: storeDetail?.data?.commercial_name ?? '',
        logo: {
            src: storeDetail?.data?.logo_img?.src,
            type: storeDetail?.data?.logo_img?.type,
            width: storeDetail?.data?.logo_img?.width,
            height: storeDetail?.data?.logo_img?.height,
            isDefaultLogo: storeDetail?.data?.logo_img?.is_default_logo,
        },
        bannerImage: storeDetail?.data?.banner_image,
        email: storeDetail?.data?.email ?? '',
        phoneNumber: storeDetail?.data?.mobile_phone_number ?? '',
        address: [
            storeDetail?.data?.address?.street ?? '',
            `${storeDetail?.data?.address?.postal_code ?? ''} - ${storeDetail?.data?.address?.city ?? ''} - ${storeDetail?.data?.address?.country ?? ''}`,
        ],
        schedule: [
            {
                header: storeDetail?.data?.schedule?.schedule_header_1 ?? '',
                text: storeDetail?.data?.schedule?.schedule_text_1 ?? '',
            },
            {
                header: storeDetail?.data?.schedule?.schedule_header_2 ?? '',
                text: storeDetail?.data?.schedule?.schedule_text_2 ?? '',
            },
        ],
        website: storeDetail?.data?.website ?? '',
        socialLinks: storeDetail?.data?.social_networks || [],
        type: storeDetail?.data?.type ?? '',
    };

    const seoImage = merchantDetail.logo?.src
        ? {
              src: merchantDetail.logo?.src || '',
              type: merchantDetail.logo?.type,
              height: merchantDetail.logo?.height,
              width: merchantDetail.logo?.width,
          }
        : merchantDetail.bannerImage?.src
          ? {
                src: merchantDetail.bannerImage?.src || '',
                type: merchantDetail.bannerImage?.type,
                height: merchantDetail.bannerImage?.height,
                width: merchantDetail.bannerImage?.width,
            }
          : null;

    const params = new URLSearchParams(window.location.search);

    useSEOMetatags({
        title: params.has('services') ? translations.servicesTitle : translations.title,
        description: params.has('services') ? translations.servicesDescription : translations.description,
        tags: [storeDetail?.data?.commercial_name || ''],
        image: seoImage
            ? {
                  ...seoImage,
                  alt: merchantDetail.name,
              }
            : undefined,
        url: SITE_URLS.merchantDetail.replace(':uuid', `${uuid}`),
        index: true,
    });

    const productHighlights = storeDetail?.data?.productHighlights
        ? storeDetail.data.productHighlights.map((product) => ({
              uuid: product.uuid ?? '',
              merchant: product.merchant ?? storeDetail?.data?.commercial_name ?? '',
              name: product.name ?? '',
              image: product.image ?? '',
              price: product.price ?? 0,
              oldPrice: product.oldPrice ?? 0,
              promo: product.promo ?? 0,
              initialFavorite: product.favorite || false,
          }))
        : [];

    return (
        <MerchantDetail
            {...merchantDetail}
            aboutText={storeDetail?.data?.about_text ?? ''}
            images={storeDetail?.data?.images || []}
            productHighlights={productHighlights}
            loading={isLoadingStoreDetail}
            locations={[
                {
                    uuid: storeDetail?.data?.uuid ?? '',
                    latitude: storeDetail?.data?.address?.latitude ?? 0,
                    longitude: storeDetail?.data?.address?.longitude ?? 0,
                },
            ]}
            services={(storeDetail?.data?.services || []).map((item) => {
                return {
                    uuid: item.uuid || '',
                    name: item.name || '',
                    description: item.description || '',
                    price: item.price || null,
                    time: item.time || null,
                    externalLink: item.external_link || null,
                    scheduleType: item.schedule_type || '',
                };
            })}
            hasProducts={storeDetail?.data?.has_products}
        />
    );
}
